import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "./card";

// Tipado mínimo para las props
interface CuotasPagosCardProps {
  loteData: {
    numero: string;
    cuotas: any[];
    totalPagado: number;
    saldoPendiente: number;
    numeroPagos: number;
    montoCuota: number;
  };
  onBack?: () => void;
}

function getCuotaEstado(cuota: any, idx: number, cuotas: any[]) {
  if (cuota.pagada) return 'pagada';
  if (cuota.vencida) return 'vencida';
  // próxima a pagar: la primera no pagada y no vencida
  const idxProxima = cuotas.findIndex((c: any) => !c.pagada && !c.vencida);
  if (idx === idxProxima) return 'proxima';
  return 'futura';
}

function getCuotaColor(estado: string) {
  switch (estado) {
    case 'pagada': return 'bg-green-100 border-green-500 text-green-700';
    case 'proxima': return 'bg-orange-100 border-orange-500 text-orange-700';
    case 'futura': return 'bg-gray-100 border-gray-300 text-gray-500';
    case 'vencida': return 'bg-red-100 border-red-500 text-red-700';
    default: return 'bg-gray-100 border-gray-300 text-gray-500';
  }
}

export const CuotasPagosCard: React.FC<CuotasPagosCardProps> = ({ loteData, onBack }) => {
  return (
    <div className="w-full relative">
      {/* Título y subtítulo */}
      <div className="mb-4">
        <div className="font-semibold text-lg text-blue-700 text-left">Lote {loteData.numero}</div>
        <div className="text-sm text-gray-600 text-left mt-1">
          Total pagado: <span className="font-bold text-green-700">Q. {loteData.totalPagado.toLocaleString()}</span> 
          &nbsp;|&nbsp; 
          Saldo pendiente: <span className="font-bold text-red-600">Q. {loteData.saldoPendiente.toLocaleString()}</span>
        </div>
        <div className="text-sm text-gray-500 text-left mt-1">
          Plan: {loteData.numeroPagos} cuotas de Q. {loteData.montoCuota.toLocaleString()} mensuales
        </div>
      </div>
      {/* Resumen de cuotas */}
      <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="text-sm text-blue-800">
          <div className="flex justify-between mb-1">
            <span>Cuotas pagadas:</span>
            <span className="font-semibold">{loteData.cuotas.filter((c: any) => c.pagada).length} de {loteData.numeroPagos}</span>
          </div>
          <div className="flex justify-between mb-1">
            <span>Cuotas pendientes:</span>
            <span className="font-semibold">{loteData.cuotas.filter((c: any) => !c.pagada).length}</span>
          </div>
          <div className="flex justify-between">
            <span>Cuotas vencidas:</span>
            <span className="font-semibold text-red-600">{loteData.cuotas.filter((c: any) => c.vencida).length}</span>
          </div>
        </div>
      </div>
      {/* Cards de cuotas */}
      <div className="space-y-3">
        {loteData.cuotas.map((cuota: any, idx: number) => {
          const estado = getCuotaEstado(cuota, idx, loteData.cuotas);
          const color = getCuotaColor(estado);
          // Formatear fecha
          const fechaFormateada = new Date(cuota.fecha).toLocaleDateString('es-GT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
          
          // Determinar el color del borde izquierdo basado en el estado
          let borderColor = 'border-l-gray-300';
          if (estado === 'pagada') borderColor = 'border-l-green-500';
          else if (estado === 'proxima') borderColor = 'border-l-red-500';
          else if (estado === 'vencida') borderColor = 'border-l-red-500';
          
          return (
            <Card key={idx} className={`bg-white border border-gray-200 border-l-4 ${borderColor} rounded-xl shadow-sm`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-4 pr-4">
                <CardTitle className="text-lg font-semibold text-gray-800">Cuota {cuota.cuotaNumero}</CardTitle>
                <div className="text-right">
                  <div className="font-bold text-lg text-gray-800">Q. {parseFloat(cuota.monto).toLocaleString()}</div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-2xl font-bold text-gray-700">{fechaFormateada}</div>
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    estado === 'pagada' ? 'bg-green-100 text-green-800' :
                    estado === 'proxima' ? 'bg-orange-100 text-orange-800' :
                    estado === 'vencida' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {estado === 'pagada' ? 'Pagada' : 
                     estado === 'proxima' ? '⏰ Próxima' : 
                     estado === 'vencida' ? '❌ Vencida' : 
                     '⏳ Pendiente'}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}; 